import { Tag } from '@isophi/pap-api';
import { mixWithWhite } from '../../utils/color.utils';

/**
 * Numbers of the MŠMT framework competences (KRAAU MŠ), keyed by the exact tag names the BE
 * `wire_questionnaire` command wires under the 6 areas. Matched by name, not id: ids differ between
 * environments, and the director's and print views get tags of both frameworks or only the selected ones.
 */
export const MSMT_COMPETENCE_NUMBERS: Readonly<Record<string, string>> = {
  'Rozumím oboru učitelství pro MŠ a dále se v něm rozvíjím': '1.1',
  'Didakticky zprostředkuji obsah dětem v souladu s jejich vzdělávacími potřebami': '1.2',
  'Nastavuji vzdělávací cíle a vedu k nastavování vlastních cílů také děti': '2.1',
  'Poznávám vzdělávací potřeby dětí a plánuji proces tak, aby se každé dítě mohlo zapojit a rozvíjet': '2.2',
  'Podporuji u dětí zvídavost a motivaci k učení': '2.3',
  'Efektivně vedu vzdělávací proces, zjišťuji porozumění dětí a reaguji na jejich potřeby': '2.4',
  'Reflektuji vzdělávací proces a vyhodnocuji dosahování cílů': '2.5',
  'Vytvářím bezpečné prostředí pro učení': '3.1',
  'Vedu děti k chování podporujícímu učení a ke spolupráci': '3.2',
  'Zajišťuji vhodné uspořádání fyzického a digitálního prostředí': '3.3',
  'Hodnotím na základě kritérií a vedu k tomu také děti': '4.1',
  'Poskytuji a přijímám zpětnou vazbu a vedu k tomu také děti': '4.2',
  'Podporuji děti v reflexi jejich učení': '4.3',
  'Spolupracuji s kolegy ve prospěch dětí a společného profesního růstu': '5.1',
  'Spolupracuji s rodiči a širší komunitou školy v zájmu dětí': '5.2',
  'Systematicky pracuji na svém profesním sebepojetí a profesním rozvoji': '6.1',
  'Odpovědně pracuji s informacemi a orientuji se v etických aspektech učitelství': '6.2',
  'Systematicky pečuji o své duševní zdraví a psychohygienu': '6.3',
};

export interface CompetenceRow {
  tag: Tag;
  number: string | null;
  color: string | null;
  help: string | null;
}

export interface CompetenceChip {
  tag: Tag;
  number: string | null;
  /** Tint of the competence's area, null for the old framework (neutral chip). */
  background: string | null;
}

export interface CompetenceGroup {
  area: Tag | null;
  rows: CompetenceRow[];
}

const normalizeName = (name: string): string => name.trim().replace(/\s+/g, ' ');

export function msmtCompetenceNumber(tag: Pick<Tag, 'name'>): string | null {
  return MSMT_COMPETENCE_NUMBERS[normalizeName(tag.name ?? '')] ?? null;
}

/** Whether the competences belong to the MŠMT framework (old records keep the old framework and layout). */
export function isMsmtFramework(tags: Tag[]): boolean {
  return tags.some((tag) => !!tag.parentId && msmtCompetenceNumber(tag) !== null);
}

const compareNumbers = (a: CompetenceRow, b: CompetenceRow): number => {
  if (!a.number || !b.number) {
    return Number(!a.number) - Number(!b.number);
  }
  const [aArea, aItem] = a.number.split('.').map(Number);
  const [bArea, bItem] = b.number.split('.').map(Number);
  return aArea - bArea || aItem - bItem;
};

/**
 * Competences grouped under their areas (tags without a parent), in the order the areas come in.
 * Competences whose area isn't among the tags - print data carries only the selected ones - end up in
 * a trailing group without an area.
 */
export function groupCompetences(tags: Tag[]): CompetenceGroup[] {
  const groups = new Map<number, CompetenceGroup>();
  const withoutArea: CompetenceGroup = { area: null, rows: [] };

  tags.filter((tag) => !tag.parentId).forEach((area) => groups.set(area.id, { area, rows: [] }));

  tags
    .filter((tag) => !!tag.parentId)
    .forEach((tag) => {
      const group = groups.get(tag.parentId) ?? withoutArea;
      group.rows.push({
        tag,
        number: msmtCompetenceNumber(tag),
        color: group.area?.color || null,
        help: tag.description || null,
      });
    });

  return [...groups.values(), withoutArea]
    .filter((group) => group.rows.length > 0)
    .map((group) => ({ ...group, rows: [...group.rows].sort(compareNumbers) }));
}

/**
 * Overview chips of the selected competences, in their original order: MŠMT competences get their number
 * and the tint of their area as on the Doporučení page; old framework competences (no number) stay neutral.
 * Areas are looked up in `tags` (the record's available competences) and the competences themselves.
 */
export function competenceChips(competences: Tag[], tags: Tag[] = []): CompetenceChip[] {
  const areaColors = new Map<number, string>();
  [...tags, ...competences]
    .filter((tag) => !tag.parentId && !!tag.color)
    .forEach((area) => areaColors.set(area.id, area.color as string));

  return competences.map((tag) => {
    const number = msmtCompetenceNumber(tag);
    const color = number && tag.parentId ? areaColors.get(tag.parentId) : undefined;
    return { tag, number, background: color ? mixWithWhite(color, 0.25) : null };
  });
}
